import apiClient from "../../services/apiClient";
import { CourseData } from "../types"; // Keep CourseData if used by update/post
// Import the centralized URIs - ADJUST PATH AS NEEDED
import { mobileAppUri, dataLoaderUri } from "../API";

// NEW: Interface for the items within the Metadata array from course-order
export interface CourseMetadataItem {
  Identifier: string;
  Vocabulary_List: string; // This is the section identifier linked to the course
}

// Updated: Interface for the overall response from course-order
interface CourseOrderApiResponse {
  Targ_Lang_Code: string;
  Domain: string;
  Metadata: CourseMetadataItem[]; // Use the new specific type here
}

// Defines the input structure for adding a new section.
interface SectionData {
  Identifier: string;
  Targ_Lang_Code: string;
  Lessons: any[];
}

// --- Fetch Sections ---
// Method: GET -> Use mobileAppUri
export async function fetchSections(langCode: string): Promise<string[]> {
  try {
    const json = await apiClient.get(`${mobileAppUri}/api/v1/all-sections`, {
      params: { Targ_Lang_Code: langCode }
    });
    // Handle both string and already-parsed responses
    const sections = typeof json.sections === 'string'
      ? JSON.parse(json.sections)
      : json.sections;
    const identifiers = sections.map((s: any) => s.Identifier);
    return identifiers;
  } catch (error) {
    console.error("Error fetching vocabulary sections:", error);
    return [];
  }
}

// --- Fetch Course Order Data (UPDATED) ---
// Method: GET -> Use mobileAppUri
export async function fetchCourseOrderData(
  langCode: string,
  domain: string = 'general'
): Promise<CourseMetadataItem[]> {
  try {
    const data: CourseOrderApiResponse = await apiClient.get(`${mobileAppUri}/api/v1/course-order`, {
      params: { Targ_Lang_Code: langCode, courseId: domain }
    });
    return data.Metadata;
  } catch (error) {
    console.error("Error fetching course order data:", error);
    throw error;
  }
}

// --- Update Full Course Data (For reference) ---
// Method: PUT -> Use dataLoaderUri
export async function updateCourseData(course: CourseData): Promise<void> {
  const url = `${dataLoaderUri}/api/v1/update-course`; // Replaced base URL
  try {
    await apiClient.put(url, course);
  } catch (error) {
    console.error("Error updating course data:", error);
    throw error;
  }
}

// --- New: Update Course Section Link ---
// This function updates the Vocabulary_List (i.e., section link) for a specific course.
// Method: PUT -> Use dataLoaderUri
export async function updateCourseSectionLink(
  targLangCode: string,
  identifier: string,
  vocabularyList: string
): Promise<void> {
  const url = `${dataLoaderUri}/api/v1/update-course`;

  const payload = {
    Targ_Lang_Code: targLangCode,
    Domain: "general",
    Metadata: [
      {
        Identifier: identifier,
        Vocabulary_List: vocabularyList,
      },
    ],
  };

  try {
    await apiClient.put(url, payload);
  } catch (error) {
    console.error("Error updating course section link:", error);
    throw error;
  }
}

// --- Batch Update Course Section Links ---
// Updates multiple courses' Vocabulary_List values in a single API call
export async function updateCourseSectionLinksBatch(
  targLangCode: string,
  updates: { Identifier: string; Vocabulary_List: string }[]
): Promise<void> {
  const url = `${dataLoaderUri}/api/v1/update-course`;

  const payload = {
    Targ_Lang_Code: targLangCode,
    Domain: "general",
    Metadata: updates,
  };

  try {
    await apiClient.put(url, payload);
  } catch (error) {
    console.error("Error batch updating course section links:", error);
    throw error;
  }
}

// --- Post Full Course Data (For reference) ---
// Method: POST -> Use dataLoaderUri
export async function postCourseData(course: {
  Targ_Lang_Code: string;
  Domain: string;
  Identifier: string;
  City: string;
  Country: string;
  Description: string;
  Images: { URL: string; Description: string }[];
  Language_Data: {
    [lang: string]: {
      Base_Lang_Description: string;
      Vocabulary_List: string;
    };
  };
}): Promise<void> {
  const url = `${dataLoaderUri}/api/v1/add-course`; // Replaced base URL
  try {
    await apiClient.post(url, course);
  } catch (error) {
    console.error("Error posting course data:", error);
    throw error;
  }
}

// --- Add Section ---
// Method: POST -> Use dataLoaderUri
export async function addSection(section: SectionData): Promise<void> {
  const url = `${dataLoaderUri}/api/v1/add-section`; // Replaced base URL
  const payload = {
    Identifier: section.Identifier,
    Targ_Lang_Code: section.Targ_Lang_Code,
    Lessons: JSON.stringify(section.Lessons), // Ensure Lessons are stringified if API expects string
  };
  try {
    await apiClient.post(url, payload);
    console.log(`Section "${section.Identifier}" added successfully.`);
  } catch (error) {
    console.error("Error adding section:", error);
    throw error;
  }
}

// --- Placeholder for Updating Course Order ---
// This function would take the language code and the new array of identifiers.
// Method: PUT -> Use dataLoaderUri
export async function updateCourseOrderApi(
  langCode: string,
  orderedIdentifiers: string[]
): Promise<void> {
  const url = `${dataLoaderUri}/api/v1/course-order`; // Replaced base URL
  console.log(
    "Attempting to save order for",
    langCode,
    "with order:",
    orderedIdentifiers
  );
  const payload = {
    Targ_Lang_Code: langCode,
    Domain: "general", // Assuming 'general' is correct
    OrderedIdentifiers: orderedIdentifiers,
  };

  try {
    await apiClient.put(url, payload);

    console.log("Course order updated successfully on the backend.");
  } catch (error) {
    console.error("Error updating course order:", error);
    throw error;
  }
}
